import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const LoginPage = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000000] flex items-center justify-center relative overflow-hidden">
      {/* Ambient glow background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-[-200px] left-[-200px] w-[600px] h-[600px] bg-[#FF007F] rounded-full opacity-[0.07] blur-[150px]" />
        <div className="absolute bottom-[-200px] right-[-200px] w-[500px] h-[500px] bg-[#39FF14] rounded-full opacity-[0.05] blur-[150px]" />
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="relative inline-block mb-5" data-testid="logo">
            {/* Dual-glow backdrop */}
            <div className="absolute -bottom-4 -left-6 w-32 h-32 rounded-full bg-[#FF007F] opacity-30 blur-3xl pointer-events-none" />
            <div className="absolute -top-4 -right-6 w-32 h-32 rounded-full bg-[#39FF14] opacity-25 blur-3xl pointer-events-none" />
            <img
              src="/brand/mb-icon.png"
              alt="MusicBox by MusicBooth"
              className="relative w-40 h-40 object-contain"
              draggable={false}
            />
          </div>
          <h1
            className="text-white text-4xl font-extrabold tracking-tight"
            style={{ fontFamily: 'Outfit', letterSpacing: '-0.02em' }}
          >
            MusicBox
          </h1>
          <p className="text-[#A0A0A0] text-xs mt-1.5 tracking-[0.3em] uppercase font-mono">
            by <span className="bg-gradient-to-r from-[#39FF14] to-[#00BFFF] bg-clip-text text-transparent font-semibold">MusicBooth</span>
          </p>
        </div>

        {/* Glass Card */}
        <div className="glass-surface rounded-2xl p-8" data-testid="login-card">
          <h2 className="text-xl font-bold text-white mb-1 tracking-wide" style={{ fontFamily: 'Outfit' }}>Welcome back</h2>
          <p className="text-[#A0A0A0] text-sm mb-7">Sign in to your venue</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#A0A0A0] text-xs uppercase tracking-widest font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@venue.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="bg-[#000000]/60 border-white/8 text-white placeholder:text-[#606060] h-12 rounded-xl focus:border-[#FF007F] focus:ring-[#FF007F]/20"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#A0A0A0] text-xs uppercase tracking-widest font-semibold">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="bg-[#000000]/60 border-white/8 text-white placeholder:text-[#606060] h-12 rounded-xl focus:border-[#FF007F] focus:ring-[#FF007F]/20"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-12 bg-[#FF007F] hover:bg-[#CC0066] text-white font-bold rounded-full tracking-wider transition-all hover:scale-[1.02] glow-purple"
            >
              {loading ? 'Signing in...' : 'SIGN IN'}
            </Button>
          </form>

          <p className="mt-6 text-center text-[#606060] text-sm">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-[#FF007F] hover:text-[#CC0066] font-semibold">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
